import { Trans } from "@lingui/react/macro";
import { captureRootError } from "@/lib/sentry";
import { useRefreshToken } from "@pontimed/auth-client";
import { VisualViewportBottomProbe } from "@pontimed/ui/lib/use-visual-viewport-bottom";
import { Outlet, createRootRoute } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { PostHogIdentityBridge } from "@/lib/analytics/identity-bridge";
import { PostHogPageviewTracker } from "@/lib/analytics/pageview-tracker";

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootComponent() {
  useRefreshToken();
  return (
    <div className="bg-background text-foreground min-h-dvh">
      {/* Visual-viewport tracker — keeps fixed bottom UI pinned to the
          visible bottom on Chrome Android. Cheap (single rAF on resize). */}
      <VisualViewportBottomProbe />
      {/* PostHog plumbing — render null components, both rely on TanStack
          Router context (useSession + useRouterState). */}
      <PostHogIdentityBridge />
      <PostHogPageviewTracker />
      <Outlet />
    </div>
  );
}

function NotFound() {
  return (
    <div className="bg-background text-foreground flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-2xl font-bold">
        <Trans>Page not found</Trans>
      </h1>
      <p className="text-muted-foreground max-w-md text-sm">
        <Trans>The admin section you are looking for has not been ported yet.</Trans>
      </p>
      <Button onClick={() => history.back()}>
        <Trans>Go back</Trans>
      </Button>
    </div>
  );
}

function RootError({ error }: { error: Error }) {
  // F6: surface route-level crashes to Sentry — this boundary was silent.
  captureRootError(error);
  return (
    <div className="bg-background text-foreground flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-2xl font-bold">
        <Trans>Something went wrong</Trans>
      </h1>
      <p className="text-muted-foreground max-w-md text-sm">{error.message}</p>
      <Button onClick={() => location.reload()}>
        <Trans>Reload</Trans>
      </Button>
    </div>
  );
}
